package org.gymly.i18n

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable

/**
 * Textos del dominio de membresías: accesos de socios, planes, órdenes de membresía
 * y órdenes de venta.
 *
 * Al ser una interfaz con dos implementaciones, si falta una traducción no compila.
 */
interface MembershipsStrings {
    // ---------- Filtros de rango de fechas (compartidos por las cuatro pantallas) ----------
    val rangeDays7: String
    val rangeDays30: String
    val rangeAll: String

    // ---------- Cabeceras y etiquetas reutilizadas ----------
    val colClient: String
    val colPlan: String
    val colTrainer: String
    val colValidity: String
    val colMode: String
    val colCreatedAt: String
    val colMethod: String
    val colItems: String
    val colOrder: String
    val noTrainer: String
    val noTrainerAssigned: String
    val actionView: String
    val actionHistory: String
    val searchPlaceholderDots: String
    val searchPlaceholderEllipsis: String
    val datePlaceholder: String
    val isoDatePlaceholder: String

    /** Cédula abreviada en la línea de contacto de una fila. */
    fun docIdShort(doc: String): String

    /** Cédula completa en el subtítulo del selector de cliente. */
    fun docIdLong(doc: String): String

    // ---------- Membresías (AdminMembershipsScreen) ----------
    val title: String
    val subtitle: String
    val enrollHint: String
    val loadFailed: String
    val kpiMembers: String
    val kpiActiveAccess: String
    val kpiExpired: String
    val kpiCancelled: String
    val accessBannerTitle: String
    val accessBannerBody: String
    val directoryTitle: String
    val manualActivationButton: String
    val emptyMemberships: String
    val accessCanEnter: String
    val accessBlocked: String
    val accessExpired: String
    val accessCancelled: String
    val historyTitle: String
    val historySubtitle: String
    val historyEmpty: String
    val detailTitle: String
    val detailStartDate: String
    val detailEndDate: String
    val detailManualReason: String
    val detailManagedBy: String
    val blockAction: String
    val releaseAction: String
    val manualActivationTitle: String
    val editTitle: String
    val manualActivationHint: String
    val clientRequired: String
    val clientSearchPlaceholder: String
    val selectPlan: String
    val endDateOptional: String
    val reasonOptional: String
    val reasonPlaceholder: String
    val errSelectClient: String
    val errSelectPlan: String
    val errInvalidStartDate: String
    val errInvalidEndDate: String
    val deleteTitle: String
    val assignFailed: String
    val saveFailed: String
    val deleteFailed: String

    /** Vencimiento bajo la vigencia de una fila. */
    fun expiresOn(date: String): String

    fun deleteConfirm(client: String): String

    // ---------- Órdenes de membresía (AdminMembershipOrdersScreen) ----------
    val ordersTitle: String
    val ordersSubtitle: String
    val ordersAccessHint: String
    val ordersLoadFailed: String
    val ordersBannerTitle: String
    val ordersBannerBody: String
    val ordersKpiTotal: String
    val ordersKpiPaid: String
    val ordersKpiPending: String
    val ordersKpiCourtesy: String
    val ordersDirectoryTitle: String
    val ordersNewButton: String
    val ordersEmpty: String
    val payAction: String
    val orderStartDate: String
    val paymentFailed: String
    val deleteOrderTitle: String
    val deleteOrderFailed: String
    val newOrderTitle: String
    val editOrderTitle: String
    val createOrderFailed: String
    val updateOrderFailed: String
    val clientOnlyMembersHint: String
    val clientSearchByPlaceholder: String
    val noMatchingMembers: String
    val planRequired: String
    val courtesyLabel: String
    val courtesyHint: String
    val amountToPay: String
    val usdReferenceHint: String
    val orderDateLabel: String
    val errSelectClientFromList: String
    val errPlanHasNoPrice: String
    val errReviewPaymentBreakdown: String
    val errNeedPaymentMethod: String

    fun orderNumberTitle(number: String): String
    fun orderPaymentRegistered(number: String): String
    fun orderCreated(number: String): String
    fun deleteOrderConfirm(number: String, client: String): String
    fun creditAppliedSummary(applied: String, due: String): String

    // ---------- Planes de membresía (AdminMembershipPlansScreen) ----------
    val plansTitle: String
    val plansSubtitle: String
    val plansLoadFailed: String
    val plansKpiTotal: String
    val plansKpiDaily: String
    val plansKpiWeekly: String
    val plansKpiMonthly: String
    val plansKpiActive: String
    val plansDirectoryTitle: String
    val plansNewButton: String
    val plansEmpty: String
    val colSchedule: String
    val colPrice: String
    val planDetailTitle: String
    val billingPeriod: String
    val priceUsd: String
    val priceBs: String
    val includedServices: String
    val allServicesIncluded: String
    val addPlanTitle: String
    val editPlanTitle: String
    val deletePlanTitle: String
    val planSaveFailed: String
    val planDeleteFailed: String
    val nameRequired: String
    val planNamePlaceholder: String
    val exchangeUnavailable: String
    val includedServicesHint: String
    val noActiveServices: String
    val errNameRequired: String
    val errInvalidPriceBs: String

    fun deletePlanConfirm(name: String): String

    /** Tasa oficial bajo el precio en bolívares; la fecha se concatena aparte. */
    fun bcvRate(rate: String): String

    /** Servicio incluido en el plan: nombre + tipo. */
    fun serviceWithType(name: String, type: String): String

    // ---------- Órdenes de venta (AdminOrdersScreen) ----------
    val salesOrdersTitle: String
    val salesOrdersSubtitle: String
    val salesOrdersLoadFailed: String
    val salesKpiTotal: String
    val salesKpiPaid: String
    val salesKpiPending: String
    val collectAction: String
    val salesOrderDetailTitle: String
    val registerPaymentAction: String
    val paymentDateRequired: String
    val referenceRequired: String
    val referenceOptional: String
    val referencePlaceholder: String
    val proofLabel: String
    val changeProof: String
    val uploadProof: String
    val noFileSelected: String
    val sendReceiptByEmail: String
    val errInvalidDate: String
    val errReferenceRequired: String
    val mailSent: String
    val mailNotSent: String
    val saveAndReceipt: String
    val newSalesOrderTitle: String
    val saveSalesOrderFailed: String
    val deleteSalesOrderFailed: String
    val clientSearchHint: String
    val clientSearchFieldPlaceholder: String
    val searchClientIcon: String
    val noClientFound: String
    val typeAtLeastTwoChars: String
    val itemsPlaceholder: String
    val totalBs: String
    val errItemsRequired: String
    val errInvalidTotal: String

    fun registerPaymentTitle(number: String): String
    fun paymentRegistered(number: String, mailNote: String): String
    fun deleteSalesOrderConfirm(number: String): String
}

internal object MembershipsEs : MembershipsStrings {
    // ---------- Filtros de rango de fechas ----------
    override val rangeDays7 = "7 días"
    override val rangeDays30 = "30 días"
    override val rangeAll = "Todos"

    // ---------- Cabeceras y etiquetas reutilizadas ----------
    override val colClient = "Cliente"
    override val colPlan = "Plan"
    override val colTrainer = "Entrenador"
    override val colValidity = "Vigencia"
    override val colMode = "Modo"
    override val colCreatedAt = "Alta"
    override val colMethod = "Método"
    override val colItems = "Ítems"
    override val colOrder = "Orden"
    override val noTrainer = "Sin entrenador"
    override val noTrainerAssigned = "Sin entrenador asignado"
    override val actionView = "Ver"
    override val actionHistory = "Historial"
    override val searchPlaceholderDots = "Buscar..."
    override val searchPlaceholderEllipsis = "Buscar…"
    override val datePlaceholder = "AAAA-MM-DD o DD/MM/AAAA"
    override val isoDatePlaceholder = "AAAA-MM-DD"
    override fun docIdShort(doc: String) = "Céd. $doc"
    override fun docIdLong(doc: String) = "Cédula $doc"

    // ---------- Membresías ----------
    override val title = "Membresías"
    override val subtitle = "Pase de acceso al gym: vigencia, estado y entrenador. No registra cobros."
    override val enrollHint = "Para inscribir o cobrar usa Órdenes de membresía."
    override val loadFailed = "No se pudieron cargar las membresías."
    override val kpiMembers = "Socios con membresía"
    override val kpiActiveAccess = "Acceso activo"
    override val kpiExpired = "Vencidas"
    override val kpiCancelled = "Canceladas"
    override val accessBannerTitle = "Acceso al gym"
    override val accessBannerBody =
        "Aquí ves si el socio puede entrar y hasta cuándo. Se crea o renueva al pagar una orden (pagada o cortesía)."
    override val directoryTitle = "Accesos de socios"
    override val manualActivationButton = "+ Activación manual"
    override val emptyMemberships = "No hay membresías todavía."
    override val accessCanEnter = "Puede entrar"
    override val accessBlocked = "Sin acceso (bloqueada)"
    override val accessExpired = "Vencida"
    override val accessCancelled = "Cancelada"
    override val historyTitle = "Historial de membresías"
    override val historySubtitle = "Todas las membresías generadas para este socio."
    override val historyEmpty = "Sin membresías registradas para este socio."
    override val detailTitle = "Detalle de la membresía"
    override val detailStartDate = "Fecha de inicio"
    override val detailEndDate = "Vigencia hasta"
    override val detailManualReason = "Motivo manual"
    override val detailManagedBy = "Gestionado por"
    override val blockAction = "Bloquear"
    override val releaseAction = "Volver a automático"
    override val manualActivationTitle = "Activación manual de membresía"
    override val editTitle = "Editar membresía"
    override val manualActivationHint =
        "Activación manual (cortesía, excepción). El cobro normal activa la membresía automáticamente."
    override val clientRequired = "Cliente *"
    override val clientSearchPlaceholder = "Buscar clientes..."
    override val selectPlan = "Seleccionar plan"
    override val endDateOptional = "Vigencia hasta (opcional)"
    override val reasonOptional = "Motivo (opcional)"
    override val reasonPlaceholder = "Cortesía, acuerdo verbal…"
    override val errSelectClient = "Selecciona un cliente."
    override val errSelectPlan = "Selecciona un plan."
    override val errInvalidStartDate = "Fecha de inicio inválida (DD/MM/AAAA o AAAA-MM-DD)."
    override val errInvalidEndDate = "Vigencia hasta inválida (DD/MM/AAAA o AAAA-MM-DD)."
    override val deleteTitle = "Eliminar membresía"
    override val assignFailed = "No se pudo asignar la membresía."
    override val saveFailed = "No se pudo guardar la membresía."
    override val deleteFailed = "No se pudo eliminar la membresía."
    override fun expiresOn(date: String) = "Vence $date"
    override fun deleteConfirm(client: String) =
        "¿Seguro que deseas eliminar la membresía de $client? Esta acción no se puede deshacer."

    // ---------- Órdenes de membresía ----------
    override val ordersTitle = "Órdenes de membresía"
    override val ordersSubtitle =
        "Cobro e inscripción: comprobante de pago, monto y método. Al pagar se activa la membresía."
    override val ordersAccessHint = "Para ver acceso y vigencia del socio usa Membresías."
    override val ordersLoadFailed = "No se pudieron cargar las órdenes de membresía."
    override val ordersBannerTitle = "Cobro / venta"
    override val ordersBannerBody =
        "Cada orden es un recibo: pendiente, pagado o cortesía. No muestra si el socio puede entrar hoy."
    override val ordersKpiTotal = "Total órdenes"
    override val ordersKpiPaid = "Cobradas"
    override val ordersKpiPending = "Por cobrar"
    override val ordersKpiCourtesy = "Cortesía"
    override val ordersDirectoryTitle = "Listado de cobros"
    override val ordersNewButton = "+ Nueva orden"
    override val ordersEmpty = "No hay órdenes todavía."
    override val payAction = "Pagar"
    override val orderStartDate = "Inicio"
    override val paymentFailed = "No se pudo registrar el pago."
    override val deleteOrderTitle = "Eliminar orden"
    override val deleteOrderFailed = "No se pudo eliminar."
    override val newOrderTitle = "Nueva orden de membresía"
    override val editOrderTitle = "Editar orden"
    override val createOrderFailed = "No se pudo crear la orden."
    override val updateOrderFailed = "No se pudo actualizar."
    override val clientOnlyMembersHint = "Solo socios (clientes activos). El personal no aparece en este listado."
    override val clientSearchByPlaceholder = "Busca por nombre, correo o cédula"
    override val noMatchingMembers = "Sin socios coincidentes. Escribe al menos 2 caracteres."
    override val planRequired = "Plan *"
    override val courtesyLabel = "Cortesía"
    override val courtesyHint = "Sin cobro. Se guarda como cortesía."
    override val amountToPay = "Monto a pagar"
    override val usdReferenceHint = "Referencia en USD: configure la tasa BCV"
    override val orderDateLabel = "Fecha inicio"
    override val errSelectClientFromList = "Selecciona un cliente de la lista."
    override val errPlanHasNoPrice = "El plan seleccionado no tiene precio."
    override val errReviewPaymentBreakdown = "Revisa el desglose de cobro."
    override val errNeedPaymentMethod = "Indica al menos un método de pago o usa el saldo del monedero."
    override fun orderNumberTitle(number: String) = "Orden $number"
    override fun orderPaymentRegistered(number: String) = "Orden $number · cobro registrado."
    override fun orderCreated(number: String) = "Orden $number registrada."
    override fun deleteOrderConfirm(number: String, client: String) = "¿Eliminar $number de $client?"
    override fun creditAppliedSummary(applied: String, due: String) =
        "Saldo aplicado: $applied · Por cobrar: $due"

    // ---------- Planes de membresía ----------
    override val plansTitle = "Planes de Membresía"
    override val plansSubtitle = "Planes en venta desde /api/membership-plans (nombre, periodo, precio, activo)."
    override val plansLoadFailed = "No se pudieron cargar los planes."
    override val plansKpiTotal = "Total de planes"
    override val plansKpiDaily = "Diarios"
    override val plansKpiWeekly = "Semanales"
    override val plansKpiMonthly = "Mensuales"
    override val plansKpiActive = "Activos"
    override val plansDirectoryTitle = "Planes de membresía"
    override val plansNewButton = "+ Nuevo plan"
    override val plansEmpty = "No hay planes todavía."
    override val colSchedule = "Horario"
    override val colPrice = "Precio"
    override val planDetailTitle = "Detalle del plan"
    override val billingPeriod = "Periodo de facturación"
    override val priceUsd = "Precio ($)"
    override val priceBs = "Precio (Bs.)"
    override val includedServices = "Servicios incluidos"
    override val allServicesIncluded = "Todos (sin restricción)"
    override val addPlanTitle = "Agregar plan"
    override val editPlanTitle = "Editar plan"
    override val deletePlanTitle = "Eliminar plan"
    override val planSaveFailed = "No se pudo guardar el plan."
    override val planDeleteFailed = "No se pudo eliminar el plan."
    override val nameRequired = "Nombre *"
    override val planNamePlaceholder = "Nombre del plan"
    override val exchangeUnavailable = "Cotización no disponible. Puedes indicar Bs. a mano."
    override val includedServicesHint =
        "Si no seleccionas ninguno, el plan permite todos los servicios (comportamiento anterior)."
    override val noActiveServices = "No hay servicios activos en el catálogo."
    override val errNameRequired = "El nombre es obligatorio."
    override val errInvalidPriceBs = "Indica un precio válido en bolívares."
    override fun deletePlanConfirm(name: String) =
        "¿Seguro que deseas eliminar «$name»? Esta acción no se puede deshacer."
    override fun bcvRate(rate: String) = "Tasa BCV · $rate Bs/\$"
    override fun serviceWithType(name: String, type: String) = "$name ($type)"

    // ---------- Órdenes de venta ----------
    override val salesOrdersTitle = "Órdenes"
    override val salesOrdersSubtitle =
        "Órdenes de venta y sus líneas desde /api/orders (cliente, ítems, total, estado, fecha)."
    override val salesOrdersLoadFailed = "No se pudieron cargar las órdenes."
    override val salesKpiTotal = "Total de órdenes"
    override val salesKpiPaid = "Pagadas"
    override val salesKpiPending = "Pendientes"
    override val collectAction = "Cobrar"
    override val salesOrderDetailTitle = "Detalle de la orden"
    override val registerPaymentAction = "Registrar pago"
    override val paymentDateRequired = "Fecha del pago *"
    override val referenceRequired = "Referencia *"
    override val referenceOptional = "Referencia (opcional)"
    override val referencePlaceholder = "Nº de referencia / operación"
    override val proofLabel = "Comprobante (imagen o PDF)"
    override val changeProof = "Cambiar comprobante"
    override val uploadProof = "Subir comprobante"
    override val noFileSelected = "No se seleccionó archivo."
    override val sendReceiptByEmail = "Enviar recibo por correo al cliente"
    override val errInvalidDate = "Fecha inválida (DD/MM/AAAA o AAAA-MM-DD)."
    override val errReferenceRequired = "La referencia es obligatoria para este método."
    override val mailSent = " · mail enviado"
    override val mailNotSent = " · mail no enviado"
    override val saveAndReceipt = "Guardar y recibo"
    override val newSalesOrderTitle = "Nueva orden"
    override val saveSalesOrderFailed = "No se pudo guardar la orden."
    override val deleteSalesOrderFailed = "No se pudo eliminar la orden."
    override val clientSearchHint = "Busca por nombre, apellido, correo o cédula."
    override val clientSearchFieldPlaceholder = "Nombre, apellido, correo o cédula…"
    override val searchClientIcon = "Buscar cliente"
    override val noClientFound = "No hay cliente con ese dato."
    override val typeAtLeastTwoChars = "Escribe al menos 2 caracteres para buscar."
    override val itemsPlaceholder = "Ej. plan mensual, suplementos…"
    override val totalBs = "Total (Bs.)"
    override val errItemsRequired = "Indica la descripción de los ítems."
    override val errInvalidTotal = "Total inválido (usa formato 45,00)."
    override fun registerPaymentTitle(number: String) = "Registrar pago · $number"
    override fun paymentRegistered(number: String, mailNote: String) =
        "Pago $number registrado · recibo descargado$mailNote"
    override fun deleteSalesOrderConfirm(number: String) =
        "¿Seguro que deseas eliminar la orden $number? Esta acción no se puede deshacer."
}

internal object MembershipsEn : MembershipsStrings {
    // ---------- Filtros de rango de fechas ----------
    override val rangeDays7 = "Last 7 days"
    override val rangeDays30 = "Last 30 days"
    override val rangeAll = "All"

    // ---------- Cabeceras y etiquetas reutilizadas ----------
    override val colClient = "Client"
    override val colPlan = "Plan"
    override val colTrainer = "Trainer"
    override val colValidity = "Validity"
    override val colMode = "Mode"
    override val colCreatedAt = "Created"
    override val colMethod = "Method"
    override val colItems = "Items"
    override val colOrder = "Order"
    override val noTrainer = "No trainer"
    override val noTrainerAssigned = "No trainer assigned"
    override val actionView = "View"
    override val actionHistory = "History"
    override val searchPlaceholderDots = "Search..."
    override val searchPlaceholderEllipsis = "Search…"
    override val datePlaceholder = "YYYY-MM-DD or DD/MM/YYYY"
    override val isoDatePlaceholder = "YYYY-MM-DD"
    override fun docIdShort(doc: String) = "ID $doc"
    override fun docIdLong(doc: String) = "ID $doc"

    // ---------- Membresías ----------
    override val title = "Memberships"
    override val subtitle = "Gym access pass: validity, status and trainer. It does not record payments."
    override val enrollHint = "To enroll or charge a member, use Membership orders."
    override val loadFailed = "Could not load memberships."
    override val kpiMembers = "Members with a membership"
    override val kpiActiveAccess = "Active access"
    override val kpiExpired = "Expired"
    override val kpiCancelled = "Cancelled"
    override val accessBannerTitle = "Gym access"
    override val accessBannerBody =
        "Here you can see whether a member can get in and until when. It is created or renewed when an order is settled (paid or courtesy)."
    override val directoryTitle = "Member access"
    override val manualActivationButton = "+ Manual activation"
    override val emptyMemberships = "No memberships yet."
    override val accessCanEnter = "Can enter"
    override val accessBlocked = "No access (blocked)"
    override val accessExpired = "Expired"
    override val accessCancelled = "Cancelled"
    override val historyTitle = "Membership history"
    override val historySubtitle = "Every membership created for this member."
    override val historyEmpty = "No memberships on record for this member."
    override val detailTitle = "Membership details"
    override val detailStartDate = "Start date"
    override val detailEndDate = "Valid until"
    override val detailManualReason = "Manual reason"
    override val detailManagedBy = "Managed by"
    override val blockAction = "Block"
    override val releaseAction = "Back to automatic"
    override val manualActivationTitle = "Manual membership activation"
    override val editTitle = "Edit membership"
    override val manualActivationHint =
        "Manual activation (courtesy, exception). A regular payment activates the membership automatically."
    override val clientRequired = "Client *"
    override val clientSearchPlaceholder = "Search clients..."
    override val selectPlan = "Select a plan"
    override val endDateOptional = "Valid until (optional)"
    override val reasonOptional = "Reason (optional)"
    override val reasonPlaceholder = "Courtesy, verbal agreement…"
    override val errSelectClient = "Select a client."
    override val errSelectPlan = "Select a plan."
    override val errInvalidStartDate = "Invalid start date (DD/MM/YYYY or YYYY-MM-DD)."
    override val errInvalidEndDate = "Invalid end date (DD/MM/YYYY or YYYY-MM-DD)."
    override val deleteTitle = "Delete membership"
    override val assignFailed = "Could not assign the membership."
    override val saveFailed = "Could not save the membership."
    override val deleteFailed = "Could not delete the membership."
    override fun expiresOn(date: String) = "Expires $date"
    override fun deleteConfirm(client: String) =
        "Are you sure you want to delete the membership for $client? This action cannot be undone."

    // ---------- Órdenes de membresía ----------
    override val ordersTitle = "Membership orders"
    override val ordersSubtitle =
        "Charge and enrollment: payment proof, amount and method. The membership is activated once paid."
    override val ordersAccessHint = "To check a member's access and validity, use Memberships."
    override val ordersLoadFailed = "Could not load membership orders."
    override val ordersBannerTitle = "Charge / sale"
    override val ordersBannerBody =
        "Each order is a receipt: pending, paid or courtesy. It does not show whether the member can get in today."
    override val ordersKpiTotal = "Total orders"
    override val ordersKpiPaid = "Collected"
    override val ordersKpiPending = "Outstanding"
    override val ordersKpiCourtesy = "Courtesy"
    override val ordersDirectoryTitle = "Charges list"
    override val ordersNewButton = "+ New order"
    override val ordersEmpty = "No orders yet."
    override val payAction = "Pay"
    override val orderStartDate = "Start"
    override val paymentFailed = "Could not record the payment."
    override val deleteOrderTitle = "Delete order"
    override val deleteOrderFailed = "Could not delete."
    override val newOrderTitle = "New membership order"
    override val editOrderTitle = "Edit order"
    override val createOrderFailed = "Could not create the order."
    override val updateOrderFailed = "Could not update."
    override val clientOnlyMembersHint = "Members only (active clients). Staff does not appear in this list."
    override val clientSearchByPlaceholder = "Search by name, email or ID"
    override val noMatchingMembers = "No matching members. Type at least 2 characters."
    override val planRequired = "Plan *"
    override val courtesyLabel = "Courtesy"
    override val courtesyHint = "No charge. Saved as a courtesy."
    override val amountToPay = "Amount due"
    override val usdReferenceHint = "USD reference: set the BCV rate"
    override val orderDateLabel = "Start date"
    override val errSelectClientFromList = "Select a client from the list."
    override val errPlanHasNoPrice = "The selected plan has no price."
    override val errReviewPaymentBreakdown = "Check the payment breakdown."
    override val errNeedPaymentMethod = "Add at least one payment method or use the digital wallet balance."
    override fun orderNumberTitle(number: String) = "Order $number"
    override fun orderPaymentRegistered(number: String) = "Order $number · payment recorded."
    override fun orderCreated(number: String) = "Order $number created."
    override fun deleteOrderConfirm(number: String, client: String) = "Delete $number for $client?"
    override fun creditAppliedSummary(applied: String, due: String) =
        "Credit applied: $applied · Outstanding: $due"

    // ---------- Planes de membresía ----------
    override val plansTitle = "Membership Plans"
    override val plansSubtitle = "Plans on sale from /api/membership-plans (name, period, price, active)."
    override val plansLoadFailed = "Could not load plans."
    override val plansKpiTotal = "Total plans"
    override val plansKpiDaily = "Daily"
    override val plansKpiWeekly = "Weekly"
    override val plansKpiMonthly = "Monthly"
    override val plansKpiActive = "Active"
    override val plansDirectoryTitle = "Membership plans"
    override val plansNewButton = "+ New plan"
    override val plansEmpty = "No plans yet."
    override val colSchedule = "Schedule"
    override val colPrice = "Price"
    override val planDetailTitle = "Plan details"
    override val billingPeriod = "Billing period"
    override val priceUsd = "Price ($)"
    override val priceBs = "Price (Bs.)"
    override val includedServices = "Included services"
    override val allServicesIncluded = "All (no restrictions)"
    override val addPlanTitle = "Add plan"
    override val editPlanTitle = "Edit plan"
    override val deletePlanTitle = "Delete plan"
    override val planSaveFailed = "Could not save the plan."
    override val planDeleteFailed = "Could not delete the plan."
    override val nameRequired = "Name *"
    override val planNamePlaceholder = "Plan name"
    override val exchangeUnavailable = "Exchange rate unavailable. You can enter Bs. manually."
    override val includedServicesHint =
        "If you select none, the plan allows every service (previous behaviour)."
    override val noActiveServices = "There are no active services in the catalog."
    override val errNameRequired = "The name is required."
    override val errInvalidPriceBs = "Enter a valid price in bolívares."
    override fun deletePlanConfirm(name: String) =
        "Are you sure you want to delete “$name”? This action cannot be undone."
    override fun bcvRate(rate: String) = "BCV rate · $rate Bs/\$"
    override fun serviceWithType(name: String, type: String) = "$name ($type)"

    // ---------- Órdenes de venta ----------
    override val salesOrdersTitle = "Orders"
    override val salesOrdersSubtitle =
        "Sales orders and their lines from /api/orders (client, items, total, status, date)."
    override val salesOrdersLoadFailed = "Could not load orders."
    override val salesKpiTotal = "Total orders"
    override val salesKpiPaid = "Paid"
    override val salesKpiPending = "Pending"
    override val collectAction = "Charge"
    override val salesOrderDetailTitle = "Order details"
    override val registerPaymentAction = "Record payment"
    override val paymentDateRequired = "Payment date *"
    override val referenceRequired = "Reference *"
    override val referenceOptional = "Reference (optional)"
    override val referencePlaceholder = "Reference / transaction no."
    override val proofLabel = "Proof (image or PDF)"
    override val changeProof = "Change proof"
    override val uploadProof = "Upload proof"
    override val noFileSelected = "No file selected."
    override val sendReceiptByEmail = "Email the receipt to the client"
    override val errInvalidDate = "Invalid date (DD/MM/YYYY or YYYY-MM-DD)."
    override val errReferenceRequired = "A reference is required for this payment method."
    override val mailSent = " · email sent"
    override val mailNotSent = " · email not sent"
    override val saveAndReceipt = "Save and receipt"
    override val newSalesOrderTitle = "New order"
    override val saveSalesOrderFailed = "Could not save the order."
    override val deleteSalesOrderFailed = "Could not delete the order."
    override val clientSearchHint = "Search by first name, last name, email or ID."
    override val clientSearchFieldPlaceholder = "First name, last name, email or ID…"
    override val searchClientIcon = "Search client"
    override val noClientFound = "No client matches that."
    override val typeAtLeastTwoChars = "Type at least 2 characters to search."
    override val itemsPlaceholder = "e.g. monthly plan, supplements…"
    override val totalBs = "Total (Bs.)"
    override val errItemsRequired = "Enter a description for the items."
    override val errInvalidTotal = "Invalid total (use the 45,00 format)."
    override fun registerPaymentTitle(number: String) = "Record payment · $number"
    override fun paymentRegistered(number: String, mailNote: String) =
        "Payment $number recorded · receipt downloaded$mailNote"
    override fun deleteSalesOrderConfirm(number: String) =
        "Are you sure you want to delete order $number? This action cannot be undone."
}

@Composable
@ReadOnlyComposable
fun membershipsStrings(): MembershipsStrings = pickStrings(MembershipsEs, MembershipsEn)
